import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { ChevronDown, MapPin, AlertCircle, ArrowLeft, Dot, ChevronRight, ChevronLeft, Check, X, Download, Crosshair, Locate } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, Circle } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axiosInstance from "../../api/axiosInstance";
import { useSearchParams } from "react-router-dom";
import { propertyTypeLabels } from "../../utils/common";
// import demoResponse from "./demo-response.json";

const scopeLabels = {
  county: "County",
  zip: "ZIP Code",
  city: "City",
};

const groupLabels = {
  flippers: "Flippers",
  landlords: "Landlords",
};

const limitOptions = [
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
  { label: "200", value: 200 },
  { label: "300", value: 300 },
];

const radiusOptions = [
  { label: "0.5 mi", value: 805 },
  { label: "1 mi", value: 1609 },
  { label: "2 mi", value: 3218 },
  { label: "3 mi", value: 4828 },
  { label: "5 mi", value: 8047 },
  { label: "10 mi", value: 16093 },
];

const countDiffOptions = [
  { label: "Any", value: "" },
  { label: "Exact match (±0)", value: 0 },
  { label: "± 1", value: 1 },
  { label: "± 2", value: 2 },
  { label: "± 3", value: 3 },
];

const sqftDiffOptions = [
  { label: "Any", value: "" },
  { label: "± 100 sqft", value: 100 },
  { label: "± 250 sqft", value: 250 },
  { label: "± 500 sqft", value: 500 },
  { label: "± 1000 sqft", value: 1000 },
];

// Colors per group
const GROUP_COLORS = {
  flippers: {
    base: "#0891b2", // Cyan
    ring: "rgba(8,145,178,0.35)",
    soft: "#ecfeff",
    text: "text-cyan-700",
    border: "border-cyan-400",
    chip: "bg-cyan-100 text-cyan-800",
  },
  landlords: {
    base: "#9333ea", // Purple
    ring: "rgba(147,51,234,0.35)",
    soft: "#faf5ff",
    text: "text-purple-700",
    border: "border-purple-400",
    chip: "bg-purple-100 text-purple-800",
  },
};

// ---- Simple rounded dot marker ----
const dotSvg = (fill, size, ring) => `
  <div style="
    width:${size}px;height:${size}px;border-radius:9999px;
    background:${fill};
    border:3px solid #fff;
    box-shadow:0 1px 4px rgba(0,0,0,0.35)${ring ? `, 0 0 0 4px ${ring}` : ""};
  "></div>
`;

const makeDotIcon = (fill, size = 18, ring = null) =>
  L.divIcon({
    html: dotSvg(fill, size, ring),
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });

const subjectIcon = makeDotIcon("#dc2626", 20, "rgba(220,38,38,0.35)"); // red-600 – main/subject property
const focusedIcon = makeDotIcon("#16a34a", 18, "rgba(22,163,74,0.5)"); // slightly larger green for focused address
const connectedIcon = makeDotIcon("#f97316", 16, "rgba(249,115,22,0.55)"); // orange – selected for connection web
const flipperIcon = makeDotIcon(GROUP_COLORS.flippers.base, 16, GROUP_COLORS.flippers.ring);
const landlordIcon = makeDotIcon(GROUP_COLORS.landlords.base, 16, GROUP_COLORS.landlords.ring);
const iconFor = (sourceType) => (sourceType === "flippers" ? flipperIcon : landlordIcon);

// Cluster icon factory
const clusterTier = (count) => {
  if (count >= 100)
    return {
      bg: "rgba(11, 53, 86, 0.5)",
      ring: "rgba(11, 53, 86, 0.35)",
      size: 44,
    };

  if (count >= 25)
    return {
      bg: "rgba(3, 105, 161, 0.5)",
      ring: "rgba(3, 105, 161, 0.35)",
      size: 36,
    };

  if (count >= 10)
    return {
      bg: "rgba(2, 132, 199, 0.5)",
      ring: "rgba(2, 132, 199, 0.35)",
      size: 30,
    };

  return {
    bg: "rgba(56, 189, 248, 0.5)",
    ring: "rgba(56, 189, 248, 0.35)",
    size: 24,
  };
};

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const { bg, ring, size } = clusterTier(count);
  return L.divIcon({
    html: `
      <div style="
        width:${size}px;height:${size}px;border-radius:9999px;
        background:${bg};box-shadow:0 0 0 6px ${ring}; background-opacity: 0.5;
        display:flex;align-items:center;justify-content:center;
        color:#fff;font-weight:700;font-size:${size >= 46 ? 14 : 12}px;
        border:2px solid #fff;
      ">${count}</div>
    `,
    className: "",
    iconSize: [size, size],
  });
};

const isValidLatLng = (lat, lng) => {
  const la = Number(lat);
  const ln = Number(lng);
  return Number.isFinite(la) && Number.isFinite(ln) && !(la === 0 && ln === 0);
};

// Fits the map to bounds for a set of points
function FitBounds({ points, trigger, zoom = 15 }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], zoom);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: zoom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, map]);
  return null;
}

// Fly to a specific point (used when clicking an address)
function FlyToPoint({ point, trigger, zoom = 16 }) {
  const map = useMap();
  useEffect(() => {
    if (point) {
      map.flyTo(point, zoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, point]);
  return null;
}

// Opens the popup after flying to a focused address
function OpenPopupOnFocus({ focusedPropertyId, flyTrigger, markerRefs }) {
  const map = useMap();

  useEffect(() => {
    if (!focusedPropertyId || flyTrigger === 0) return;

    const onMoveEnd = () => {
      const marker = markerRefs.current[focusedPropertyId];
      if (marker && marker.openPopup) {
        marker.openPopup();
      }
    };

    map.once("moveend", onMoveEnd);
    return () => {
      map.off("moveend", onMoveEnd);
    };
  }, [focusedPropertyId, flyTrigger, map, markerRefs]);

  return null;
}

// ---- Skeleton loaders ----
function PropertyCardSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm flex flex-col animate-pulse">
      <div className="px-3.5 py-3">
        <div className="h-3.5 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-3" />
      </div>
    </div>
  );
}

function OwnerRowSkeleton() {
  return (
    <div className="px-4 py-3 border-b border-gray-100 animate-pulse">
      <div className="h-3.5 bg-gray-200 rounded w-1/2 mb-2" />
      <div className="h-3 bg-gray-200 rounded w-1/3" />
    </div>
  );
}

// ---- Single owner row inside a scope ----
function OwnerRow({ row, index, isSelected, colors, onClick, groupKey, scopeKey, onToggleConnectOwner, connectionState }) {
  const validAddresses = (row.addresses || []).filter((a) => isValidLatLng(a.latitude, a.longitude));
  const { count: selectedCount } = connectionState;

  return (
    <div className={`ml-2 border-b border-gray-100 ${isSelected ? `${colors.border} border-l-4` : ""}`} style={isSelected ? { backgroundColor: colors.soft } : undefined}>
      <div className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors">
        {validAddresses.length > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleConnectOwner(row);
            }}
            title={connectionState.state === "all" ? "Deselect all properties" : "Select all properties"}
            aria-label={connectionState.state === "all" ? "Deselect owner" : "Select owner"}
            className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors
              ${connectionState.state === "all" ? "bg-red-600 border-red-600" : connectionState.state === "some" ? "bg-white border-red-500" : "border-gray-300 hover:border-red-400"}`}
          >
            {connectionState.state === "all" && <Check size={11} strokeWidth={3} className="text-white" />}
            {connectionState.state === "some" && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
          </button>
        ) : (
          <div className="w-4"></div>
        )}
        <button type="button" onClick={onClick} className="flex-1 min-w-0 text-left">
          <div className={`text-sm font-medium truncate ${validAddresses.length > 0 ? "text-gray-900" : "text-red-700"}`}>
            <span>{row.name || "Unknown"}</span>
            {/* {validAddresses.length < 1 && <span className="">incorrect coords</span>} */}
          </div>
          <div className="text-xs text-gray-500 truncate">Mailing: {row.mailing_address || "Not Found"}</div>
        </button>
        {selectedCount > 0 && <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{selectedCount} selected</span>}
        <span className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full ${colors.chip}`}>{row.count || 0}</span>
      </div>
    </div>
  );
}

// ---- Collapsible scope block ----
function ScopeBlock({ scopeLabel, scopeData, isOpen, onToggle, colors, selectedKey, onSelectOwner, groupKey, scopeKey, loading, expandedOwners, onToggleExpandOwner, focusedPropertyId, onAddressClick, connectedAddresses, onToggleConnectOwner, onToggleConnectProperty }) {
  const rows = Array.isArray(scopeData) ? scopeData : scopeData?.rows || [];
  const total = Array.isArray(scopeData) ? rows.length : scopeData?.total || 0;

  return (
    <>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button type="button" onClick={onToggle} className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
          <span className="text-sm font-semibold text-gray-800">{scopeLabel}</span>
          <span className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">{total.toLocaleString()} total</span>
            <ChevronDown size={16} className={`text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </span>
        </button>
        {isOpen && (
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => <OwnerRowSkeleton key={i} />)
            ) : rows.length > 0 ? (
              rows.map((row, index) => {
                const key = `${groupKey}-${scopeKey}-${index}`;
                const validAddrIds = (row.addresses || []).filter((a) => isValidLatLng(a.latitude, a.longitude)).map((a) => a.property_id);
                const selectedIds = new Set(connectedAddresses.filter((c) => c.ownerKey === key).map((c) => c.property_id));
                const connectionState = {
                  propertyIds: selectedIds,
                  count: selectedIds.size,
                  state: selectedIds.size === 0 ? "none" : selectedIds.size >= validAddrIds.length ? "all" : "some",
                };
                return <OwnerRow key={key} row={row} index={index} colors={colors} isSelected={selectedKey === key} onClick={() => onSelectOwner({ key, groupKey, scopeKey, row, rowIndex: index })} groupKey={groupKey} scopeKey={scopeKey} connectionState={connectionState} onToggleConnectOwner={() => onToggleConnectOwner(row, key, groupKey, scopeKey, index)} />;
              })
            ) : (
              <div className="px-4 py-4 text-sm text-gray-500 text-center">No records found.</div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ---- Summary of everyone currently selected for the red connection lines ----
function SelectedOwnersSummary({ connectedAddresses, onClearOwner, onRemoveProperty, onClearAll, onAddressClick, focusedPropertyId }) {
  const [expandedSummaryOwners, setExpandedSummaryOwners] = useState({});

  if (!connectedAddresses || connectedAddresses.length === 0) return null;

  // Group selections by owner
  const ownerMap = new Map();
  connectedAddresses.forEach((a) => {
    const key = a.ownerKey || a.property_id;
    if (!ownerMap.has(key)) {
      ownerMap.set(key, { ownerKey: key, ownerName: a.ownerName || "Unknown", ownerRow: a.ownerRow, groupKey: a.groupKey, scopeKey: a.scopeKey, rowIndex: a.rowIndex, properties: [] });
    }
    ownerMap.get(key).properties.push(a);
  });
  const owners = Array.from(ownerMap.values());

  return (
    <div className="mb-4 border border-red-200 rounded-xl overflow-hidden bg-red-50/40">
      <div className="flex items-center justify-between px-4 py-2.5 bg-red-100/70">
        <span className="text-sm font-bold text-red-800">
          Selected for connection lines · {connectedAddresses.length} propert{connectedAddresses.length === 1 ? "y" : "ies"} · {owners.length} owner{owners.length === 1 ? "" : "s"}
        </span>
        <button type="button" onClick={onClearAll} className="text-xs font-semibold text-red-700 hover:text-red-900 underline">
          Clear all
        </button>
      </div>
      <div className="max-h-56 overflow-y-auto divide-y divide-red-100">
        {owners.map((owner) => {
          const isExpanded = !!expandedSummaryOwners[owner.ownerKey];
          return (
            <div key={owner.ownerKey} className="px-4 py-2">
              <div className="flex items-center justify-between gap-2">
                <button type="button" onClick={() => setExpandedSummaryOwners((prev) => ({ ...prev, [owner.ownerKey]: !prev[owner.ownerKey] }))} className="flex-1 min-w-0 flex items-center gap-1.5 text-left">
                  <ChevronDown size={13} className={`shrink-0 text-red-500 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                  <span className="text-sm font-medium text-gray-900 truncate">{owner.ownerName}</span>
                  <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-200 text-red-800">{owner.properties.length}</span>
                </button>
                <button type="button" onClick={() => onClearOwner(owner.ownerKey)} aria-label="Remove owner from selection" className="shrink-0 p-1 rounded hover:bg-red-200 transition-colors">
                  <X size={14} className="text-red-600" />
                </button>
              </div>
              {isExpanded && (
                <div className="mt-1.5 ml-5 space-y-1">
                  {owner.properties.map((p) => (
                    <div key={p.property_id} className={`flex items-center justify-between gap-2 text-xs rounded px-1.5 py-1 ${focusedPropertyId === p.property_id ? "bg-green-100 ring-1 ring-green-400" : "hover:bg-white"}`}>
                      <button type="button" onClick={() => onAddressClick({ property_id: p.property_id, latitude: p.lat, longitude: p.lng, label: p.label }, owner.groupKey, owner.scopeKey, owner.rowIndex, owner.ownerRow || { name: owner.ownerName })} className="flex-1 min-w-0 text-left text-gray-600 hover:underline truncate">
                        {p.label}
                      </button>
                      <button type="button" onClick={() => onRemoveProperty(p.property_id)} aria-label="Remove property from selection" className="shrink-0 p-0.5 rounded hover:bg-red-200">
                        <X size={11} className="text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Top-level owner group ----
function OwnerGroup({ title, groupKey, groupData, colors, isOpen, onToggleGroup, openScopes, onToggleScope, selectedKey, onSelectOwner, loading, expandedOwners, onToggleExpandOwner, focusedPropertyId, onAddressClick, connectedAddresses, onToggleConnectOwner, onToggleConnectProperty }) {
  const groupTotal = useMemo(() => Object.keys(scopeLabels).reduce((sum, scopeKey) => sum + (groupData?.[scopeKey]?.total || 0), 0), [groupData]);

  return (
    <>
      <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
        <button type="button" onClick={onToggleGroup} className="w-full flex items-center justify-between px-4 py-3" style={{ backgroundColor: colors.soft }}>
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.base }} />
            <span className={`text-base font-bold ${colors.text}`}>{title}</span>
          </span>
          <span className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${colors.chip}`}>{loading ? "..." : `${groupTotal.toLocaleString()} total`}</span>
            <ChevronDown size={18} className={`text-gray-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </span>
        </button>
        {isOpen && (
          <div className="p-3 space-y-2 bg-white">
            {Object.entries(scopeLabels).map(([scopeKey, scopeLabel]) => (
              <ScopeBlock key={scopeKey} scopeLabel={scopeLabel} scopeData={groupData?.[scopeKey]} isOpen={!!openScopes[`${groupKey}-${scopeKey}`]} onToggle={() => onToggleScope(`${groupKey}-${scopeKey}`)} colors={colors} selectedKey={selectedKey} onSelectOwner={onSelectOwner} groupKey={groupKey} scopeKey={scopeKey} loading={loading} expandedOwners={expandedOwners} onToggleExpandOwner={onToggleExpandOwner} focusedPropertyId={focusedPropertyId} onAddressClick={onAddressClick} connectedAddresses={connectedAddresses} onToggleConnectOwner={onToggleConnectOwner} onToggleConnectProperty={onToggleConnectProperty} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ---- Owner profile panel (slides in over the list) ----
function OwnerProfilePanel({ selectedOwner, onBack, onAddressClick, focusedPropertyId, connectedAddresses, onToggleConnectOwner, onToggleConnectProperty }) {
  const addresses = selectedOwner.row.addresses || [];
  const ownerName = selectedOwner.row.name || "Unknown";
  const scopeLabel = scopeLabels[selectedOwner.scopeKey];
  const groupLabel = groupLabels[selectedOwner.groupKey];
  const mailing = selectedOwner.row.mailing_address || "Not Found";

  const validAddresses = addresses.filter((a) => isValidLatLng(a.latitude, a.longitude));
  const selectedIds = new Set(connectedAddresses.filter((c) => c.ownerKey === selectedOwner.key).map((c) => c.property_id));
  const selectAllState = selectedIds.size === 0 ? "none" : selectedIds.size >= validAddresses.length ? "all" : "some";

  // Static summary placeholders
  const summary1 = "Owner has 80% purchased Single Family Homes with 2 bedrooms and 2 bathrooms";
  const summary2 = `Owner has ${addresses.length} properties matching your search property (2 beds, 2 baths, Single Family Home)`;
  const summary3 = "Last purchases for this owner: 10. March 2026 (3 months ago)";

  return (
    <div className="flex flex-col h-full bg-white" style={{ animation: "slideInRight 0.3s ease-out" }}>
      {/* Back button + owner info header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
        <button onClick={onBack} className="p-1 rounded hover:bg-gray-100" aria-label="Back to list">
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="min-w-0 flex flex-col items-center justify-center w-full">
          <div className="font-bold text-lg text-gray-900 truncate">{ownerName}</div>
          <div className="text-xs text-gray-500 truncate">{mailing}</div>
          <div className="text-xs text-gray-400">
            Found in {scopeLabel} {groupLabel}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="p-4 space-y-2 text-sm text-gray-700 border-b border-gray-100 text-left">
        <p className="flex items-start space-x-2">
          <Dot />
          <span>{summary1}</span>
        </p>
        <p className="flex items-start space-x-2">
          <Dot />
          <span>{summary2}</span>
        </p>
        <p className="flex items-start space-x-2">
          <Dot />
          <span>{summary3}</span>
        </p>
      </div>

      {/* Properties list */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-gray-500">Properties</div>
          {validAddresses.length > 0 && (
            <button type="button" onClick={() => onToggleConnectOwner(selectedOwner.row)} className="flex items-center gap-1.5 text-xs font-medium text-red-700 hover:text-red-800">
              <span
                className={`w-4 h-4 rounded border flex items-center justify-center transition-colors
                  ${selectAllState === "all" ? "bg-red-600 border-red-600" : selectAllState === "some" ? "bg-white border-red-500" : "border-gray-300"}`}
              >
                {selectAllState === "all" && <Check size={11} strokeWidth={3} className="text-white" />}
                {selectAllState === "some" && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
              </span>
              Select all
            </button>
          )}
        </div>
        {addresses.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-4">No addresses available</div>
        ) : (
          addresses.map((addr, i) => {
            const valid = isValidLatLng(addr.latitude, addr.longitude);
            const isHighlighted = focusedPropertyId === addr.property_id;
            const isChecked = valid && selectedIds.has(addr.property_id);
            return (
              <div
                key={addr.property_id ?? i}
                className={`w-full flex items-start gap-2 text-xs rounded px-2 py-1.5 mb-1 transition-colors
                  ${isHighlighted ? "bg-green-100 ring-1 ring-green-400" : ""}
                  ${!valid ? "text-red-600 bg-red-50" : "text-gray-600 hover:bg-gray-100"}`}
              >
                {valid ? (
                  <button type="button" onClick={() => onToggleConnectProperty(addr, selectedOwner.row)} aria-label={isChecked ? "Deselect property" : "Select property"} className={`mt-0.5 shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${isChecked ? "bg-red-600 border-red-600" : "border-gray-300 hover:border-red-400 bg-white"}`}>
                    {isChecked && <Check size={9} strokeWidth={3} className="text-white" />}
                  </button>
                ) : (
                  <AlertCircle size={12} className="mt-0.5 shrink-0 text-red-500" />
                )}
                <button type="button" onClick={() => onAddressClick(addr)} disabled={!valid} className="flex-1 min-w-0 text-left hover:underline disabled:cursor-not-allowed disabled:no-underline">
                  <span className="truncate">
                    {addr.label || addr.address}
                    {addr.city ? `, ${addr.city}` : ""}
                    {addr.state ? `, ${addr.state}` : ""} {addr.zip || ""}
                    {!valid && <span className="ml-1 italic">(invalid coords)</span>}
                  </span>
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// Flies to the subject property once its position becomes available
// (MapContainer's center/zoom props only apply on initial mount, so this
// is needed since subjectPosition loads asynchronously after fetchData runs).
// Falls back to New York if no valid subject coordinates were found.
const DEFAULT_CENTER = [40.7128, -74.006];

function FocusSubjectProperty({ position, loading, zoom = 11, fallbackZoom = 8, trigger }) {
  const map = useMap();

  useEffect(() => {
    if (loading) return;

    if (position) {
      map.flyTo(position, zoom);
    } else {
      map.flyTo(DEFAULT_CENTER, fallbackZoom);
    }
  }, [position, loading, trigger]);

  return null;
}

function ResizeMap({ trigger }) {
  const map = useMap();

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 300);
  }, [trigger, map]);

  return null;
}

export default function AddressOwnerGroupsV2() {
  const [searchLoading, setSearchLoading] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const addressParam = searchParams.get("address");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  // Panel open states (groups / scopes)
  const [openGroups, setOpenGroups] = useState({ flippers: true, landlords: false });
  const [openScopes, setOpenScopes] = useState({ "flippers-county": true });

  // Which owner row is selected (drives map markers & profile panel)
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Expanded owner rows (for inline address lists) – still used in OwnerRow
  const [expandedOwners, setExpandedOwners] = useState({});

  // Focused address (map + list highlight)
  const [focusedPropertyId, setFocusedPropertyId] = useState(null);
  const [flyToPoint, setFlyToPoint] = useState(null);
  const [flyTrigger, setFlyTrigger] = useState(0);

  const [panelMode, setPanelMode] = useState("both");

  const [searchRadius, setSearchRadius] = useState(3218); // 2 miles default
  const [focusSubjectTrigger, setFocusSubjectTrigger] = useState(0);

  const [exporting, setExporting] = useState(false);

  // Properties selected (shift/cmd-click on a marker) to draw connecting web lines between
  const [connectedAddresses, setConnectedAddresses] = useState([]);

  const markerRefs = useRef({});
  const clusterGroupRef = useRef(null);

  const parseDiffParam = (val) => {
    if (val === null || val === undefined || val === "") return "";
    const num = Number(val);
    return Number.isFinite(num) ? num : "";
  };

  const [formData, setFormData] = useState({
    address: searchParams.get("address") || "",
    flippersLimit: Number(searchParams.get("flippersLimit")) || 10,
    landlordsLimit: Number(searchParams.get("landlordsLimit")) || 10,
    property_type: searchParams.get("property_type") || "",
    radius: Number(searchParams.get("radius")) || 3218,
    bedroomsDiff: parseDiffParam(searchParams.get("bedroomsDiff")),
    bathroomsDiff: parseDiffParam(searchParams.get("bathroomsDiff")),
    sqftDiff: parseDiffParam(searchParams.get("sqftDiff")),
  });

  // ---- Subject property ----
  const subjectProperty = data?.subjectProperty || null;
  const headerLabel = subjectProperty ? subjectProperty.label || `${subjectProperty.address}, ${subjectProperty.city}, ${subjectProperty.state} ${subjectProperty.zip}` : addressParam || "your search";

  const subjectPosition = useMemo(() => {
    if (subjectProperty && isValidLatLng(subjectProperty.latitude, subjectProperty.longitude)) {
      return [Number(subjectProperty.latitude), Number(subjectProperty.longitude)];
    }
    return null;
  }, [subjectProperty]);

  // ---- Addresses of the selected owner ----
  const selectedOwnerAddresses = useMemo(() => {
    if (!selectedOwner?.row?.addresses) return [];
    const { groupKey, scopeKey, rowIndex } = selectedOwner;
    return selectedOwner.row.addresses
      .filter((a) => isValidLatLng(a.latitude, a.longitude))
      .map((a) => ({
        ...a,
        lat: Number(a.latitude),
        lng: Number(a.longitude),
        groupKey,
        scopeKey,
        rowIndex,
      }));
  }, [selectedOwner]);

  const ownerPoints = useMemo(() => selectedOwnerAddresses.map((a) => [a.lat, a.lng]), [selectedOwnerAddresses]);

  // ---- All properties across every currently open scope (county/zip/city) ----
  // Lets the map show every property for a location, not just the selected owner's.
  const allScopeAddresses = useMemo(() => {
    if (!data) return [];
    const result = [];
    Object.entries(openScopes).forEach(([key, isOpen]) => {
      if (!isOpen) return;
      const [groupKey, scopeKey] = key.split("-");
      const scopeData = data?.[groupKey]?.[scopeKey];
      const rows = Array.isArray(scopeData) ? scopeData : scopeData?.rows || [];
      rows.forEach((row, rowIndex) => {
        (row.addresses || []).forEach((addr) => {
          if (!isValidLatLng(addr.latitude, addr.longitude)) return;
          result.push({
            ...addr,
            lat: Number(addr.latitude),
            lng: Number(addr.longitude),
            groupKey,
            scopeKey,
            rowIndex,
            row,
          });
        });
      });
    });
    return result;
  }, [data, openScopes]);

  const mapCenter = useMemo(() => {
    if (subjectPosition) return subjectPosition;
    return DEFAULT_CENTER;
  }, [subjectPosition]);

  // ---- Handlers ----
  const handleToggleGroup = (groupKey) => setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));

  const handleToggleScope = (key) => setOpenScopes((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSelectOwner = (owner) => {
    // When clicking an owner row, open the profile panel instead of just toggling
    setSelectedOwner(owner);
    setFocusedPropertyId(null);
    setFlyToPoint(null);
  };

  const handleToggleExpandOwner = (key) => setExpandedOwners((prev) => ({ ...prev, [key]: !prev[key] }));

  // Address click from list (used in OwnerRow and OwnerProfilePanel)
  const handleAddressClick = useCallback((addr, groupKey, scopeKey, rowIndex, row) => {
    setFocusedPropertyId(addr.property_id);

    if (isValidLatLng(addr.latitude, addr.longitude)) {
      setFlyToPoint([Number(addr.latitude), Number(addr.longitude)]);
      setFlyTrigger((t) => t + 1);
    }

    // If the owner isn't already selected, select them (this also opens the profile)
    const ownerKey = `${groupKey}-${scopeKey}-${rowIndex}`;
    setSelectedOwner((prev) => {
      if (prev && prev.key === ownerKey) return prev;
      return { key: ownerKey, groupKey, scopeKey, row, rowIndex };
    });

    // Expand the relevant sections in the background (for when they go back)
    setOpenGroups((prev) => ({ ...prev, [groupKey]: true }));
    setOpenScopes((prev) => ({ ...prev, [`${groupKey}-${scopeKey}`]: true }));
    setExpandedOwners((prev) => ({ ...prev, [ownerKey]: true }));
  }, []);

  // Address click specifically from profile panel (owner already selected)
  const handleOwnerProfileAddressClick = useCallback(
    (addr) => {
      if (!selectedOwner) return;
      handleAddressClick(addr, selectedOwner.groupKey, selectedOwner.scopeKey, selectedOwner.rowIndex, selectedOwner.row);
    },
    [handleAddressClick, selectedOwner],
  );

  // Marker click from map
  const handleMarkerClick = useCallback((addr) => {
    const { groupKey, scopeKey, rowIndex } = addr;
    setFocusedPropertyId(addr.property_id);

    const ownerKey = `${groupKey}-${scopeKey}-${rowIndex}`;
    setOpenGroups((prev) => ({ ...prev, [groupKey]: true }));
    setOpenScopes((prev) => ({ ...prev, [`${groupKey}-${scopeKey}`]: true }));
    setExpandedOwners((prev) => ({ ...prev, [ownerKey]: true }));
  }, []);

  // Shift/Cmd/Ctrl-click a marker to add or remove it from the selection (tagged with the
  // same ownerKey used by the list checkboxes, so map + list selections stay in sync)
  const toggleConnectAddress = useCallback((addr) => {
    const ownerKey = `${addr.groupKey}-${addr.scopeKey}-${addr.rowIndex}`;
    setConnectedAddresses((prev) => {
      const exists = prev.some((a) => a.property_id === addr.property_id);
      if (exists) return prev.filter((a) => a.property_id !== addr.property_id);
      return [
        ...prev,
        {
          property_id: addr.property_id,
          lat: addr.lat,
          lng: addr.lng,
          label: addr.label || addr.address,
          ownerKey,
          ownerName: addr.row?.name || "Unknown",
          ownerRow: addr.row,
          groupKey: addr.groupKey,
          scopeKey: addr.scopeKey,
          rowIndex: addr.rowIndex,
        },
      ];
    });
  }, []);

  const clearConnectedAddresses = useCallback(() => setConnectedAddresses([]), []);

  // Select/deselect ALL of an owner's valid addresses at once. Every entry is tagged
  // with ownerKey/ownerName so the tri-state checkbox and the summary panel can group by owner.
  const toggleConnectOwner = useCallback((row, ownerKey, groupKey, scopeKey, rowIndex) => {
    const validAddrs = (row.addresses || []).filter((a) => isValidLatLng(a.latitude, a.longitude));
    if (validAddrs.length === 0) return;

    setConnectedAddresses((prev) => {
      const selectedForOwner = prev.filter((p) => p.ownerKey === ownerKey).length;
      const fullySelected = selectedForOwner >= validAddrs.length;

      if (fullySelected) {
        // Deselect: drop every address belonging to this owner
        return prev.filter((p) => p.ownerKey !== ownerKey);
      }

      // Select: (re)add every valid address for this owner
      const others = prev.filter((p) => p.ownerKey !== ownerKey);
      const toAdd = validAddrs.map((a) => ({
        property_id: a.property_id,
        lat: Number(a.latitude),
        lng: Number(a.longitude),
        label: a.label || a.address,
        ownerKey,
        ownerName: row.name || "Unknown",
        ownerRow: row,
        groupKey,
        scopeKey,
        rowIndex,
      }));
      return [...others, ...toAdd];
    });
  }, []);

  // Select/deselect a single property inside an owner
  const toggleConnectProperty = useCallback((addr, row, ownerKey, groupKey, scopeKey, rowIndex) => {
    if (!isValidLatLng(addr.latitude, addr.longitude)) return;
    setConnectedAddresses((prev) => {
      const exists = prev.some((p) => p.property_id === addr.property_id);
      if (exists) return prev.filter((p) => p.property_id !== addr.property_id);
      return [
        ...prev,
        {
          property_id: addr.property_id,
          lat: Number(addr.latitude),
          lng: Number(addr.longitude),
          label: addr.label || addr.address,
          ownerKey,
          ownerName: row.name || "Unknown",
          ownerRow: row,
          groupKey,
          scopeKey,
          rowIndex,
        },
      ];
    });
  }, []);

  // Remove every selected property belonging to a single owner (used by the summary panel)
  const clearOwnerConnections = useCallback((ownerKey) => {
    setConnectedAddresses((prev) => prev.filter((p) => p.ownerKey !== ownerKey));
  }, []);

  // Remove a single selected property by id (used by the summary panel)
  const removeConnectedProperty = useCallback((propertyId) => {
    setConnectedAddresses((prev) => prev.filter((p) => p.property_id !== propertyId));
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!addressParam?.trim()) {
        setError("Address is required in URL query parameter.");
        setData(null);
        return;
      }

      try {
        setLoading(true);
        setData(null);
        setError("");
        setSearchLoading(true);
        setSelectedOwner(null);
        setFocusedPropertyId(null);
        setConnectedAddresses([]);
        const params = {
          address: addressParam,
          flippers_limit: searchParams.get("flippersLimit") || undefined,
          landlords_limit: searchParams.get("landlordsLimit") || undefined,
          property_type: searchParams.get("property_type") || undefined,
          bedrooms_diff: searchParams.get("bedroomsDiff") || undefined,
          bathrooms_diff: searchParams.get("bathroomsDiff") || undefined,
          sqft_diff: searchParams.get("sqftDiff") || undefined,
        };
        const resp = await axiosInstance.get(`/api/dealmachine_properties/public/address-groups?include_addresses=true`, { params });
        setData(resp.data || null);
        // setData(demoResponse);
      } catch (err) {
        setData(null);
        setError(err.response?.data?.message || "Failed to load address data.");
      } finally {
        setLoading(false);
        setSearchLoading(false);
      }
    };

    fetchData(); // eslint-disable-next-line
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams();

    if (formData.address.trim()) newParams.set("address", formData.address.trim());
    if (formData.flippersLimit) newParams.set("flippersLimit", formData.flippersLimit);
    if (formData.landlordsLimit) newParams.set("landlordsLimit", formData.landlordsLimit);
    if (formData.property_type) newParams.set("property_type", formData.property_type);
    if (formData.bedroomsDiff !== "" && formData.bedroomsDiff !== null && formData.bedroomsDiff !== undefined) newParams.set("bedroomsDiff", formData.bedroomsDiff);
    if (formData.bathroomsDiff !== "" && formData.bathroomsDiff !== null && formData.bathroomsDiff !== undefined) newParams.set("bathroomsDiff", formData.bathroomsDiff);
    if (formData.sqftDiff !== "" && formData.sqftDiff !== null && formData.sqftDiff !== undefined) newParams.set("sqftDiff", formData.sqftDiff);

    setSearchParams(newParams);
  };

  const handlePanelMode = (mode) => {
    setPanelMode((prev) => (prev === mode ? "both" : mode));
  };

  const downloadSelectedCSV = useCallback(() => {
    if (!connectedAddresses || connectedAddresses.length === 0) return;

    try {
      setExporting(true);

      // Group selections by owner so we can compute per-owner selected/total counts
      const ownerMap = new Map();
      connectedAddresses.forEach((a) => {
        const key = a.ownerKey;
        if (!ownerMap.has(key)) {
          ownerMap.set(key, {
            groupKey: a.groupKey,
            scopeKey: a.scopeKey,
            ownerName: a.ownerName || "Unknown",
            mailingAddress: a.ownerRow?.mailing_address || "Not Found",
            totalCount: a.ownerRow?.count || 0,
            properties: [],
          });
        }
        ownerMap.get(key).properties.push(a);
      });

      const headers = ["Type", "Name", "Location", "Mailing Address", "Selected Property Address", "Selected", "Total Properties"];
      const csvRows = [];

      Array.from(ownerMap.values()).forEach((owner) => {
        const typeLabel = owner.groupKey === "flippers" ? "Flipper" : "Landlord";
        const locationLabel = scopeLabels[owner.scopeKey] || owner.scopeKey;
        const selectedCount = owner.properties.length;

        owner.properties.forEach((p) => {
          csvRows.push({
            Type: typeLabel,
            Name: owner.ownerName,
            Location: locationLabel,
            "Mailing Address": owner.mailingAddress,
            "Selected Property Address": p.label || "Unknown Address",
            Selected: selectedCount,
            "Total Properties": owner.totalCount,
          });
        });
      });

      const escapeCSV = (value) => {
        if (value === null || value === undefined) return "";
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      };

      const csvContent = [headers.map(escapeCSV).join(","), ...csvRows.map((row) => headers.map((h) => escapeCSV(row[h])).join(","))].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `address-owner-data-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error", err);
    } finally {
      setExporting(false);
    }
  }, [connectedAddresses]);

  return (
    <>
      <div className="w-screen h-screen bg-gray-100 p-4">
        {/* Animation keyframes for slide-in */}
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>

        <div className="flex flex-col h-full gap-4">
          {/* Header form */}
          <div className="bg-white border-b border-gray-200 px-5 py-3.5 text-sm">
            <form onSubmit={handleSubmit} className="!grid sm:!grid-cols-2 md:!grid-cols-3 lg:!grid-cols-4 gap-2 sm:gap-2 md:gap-2 max-w-7xl mx-auto">
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                <input value={formData.address} onChange={(e) => handleInputChange("address", e.target.value)} disabled={searchLoading} placeholder="Enter an address, city, zip" className="border-none outline-none bg-transparent w-full text-[#0B2436] disabled:cursor-not-allowed disabled:text-gray-500" />
              </div>
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                {formData.flippersLimit ? (
                  <label htmlFor="flippers" className="text-nowrap">
                    Flippers :
                  </label>
                ) : (
                  <></>
                )}
                <select className="w-full outline-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-500" value={formData.flippersLimit} disabled={searchLoading} onChange={(e) => handleInputChange("flippersLimit", Number(e.target.value))} name="flippers">
                  <option value="" disabled>
                    -- Select Flippers --
                  </option>
                  {limitOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                {formData.landlordsLimit ? (
                  <label htmlFor="landlords" className="text-nowrap">
                    Landlords :
                  </label>
                ) : (
                  <></>
                )}
                <select className="w-full outline-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-500" value={formData.landlordsLimit} disabled={searchLoading} onChange={(e) => handleInputChange("landlordsLimit", Number(e.target.value))} name="landlords">
                  <option value="" disabled>
                    -- Select Landlords --
                  </option>
                  {limitOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                {formData.property_type ? (
                  <label htmlFor="type" className="text-nowrap">
                    Type :
                  </label>
                ) : (
                  <></>
                )}
                <select className="w-full outline-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-500" value={formData.property_type || ""} disabled={searchLoading} onChange={(e) => handleInputChange("property_type", e.target.value)} name="type">
                  <option value="">-- Select Property Type --</option>
                  {Object.entries(propertyTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                <label className="text-nowrap">Radius :</label>

                <select className="w-full outline-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-500" value={searchRadius} disabled={searchLoading} onChange={(e) => setSearchRadius(Number(e.target.value))}>
                  {radiusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                <label className="text-nowrap">Beds :</label>
                <select className="w-full outline-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-500" value={formData.bedroomsDiff} disabled={searchLoading} onChange={(e) => handleInputChange("bedroomsDiff", e.target.value === "" ? "" : Number(e.target.value))}>
                  {countDiffOptions.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                <label className="text-nowrap">Baths :</label>
                <select className="w-full outline-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-500" value={formData.bathroomsDiff} disabled={searchLoading} onChange={(e) => handleInputChange("bathroomsDiff", e.target.value === "" ? "" : Number(e.target.value))}>
                  {countDiffOptions.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                <label className="text-nowrap">Sqft :</label>
                <select className="w-full outline-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-500" value={formData.sqftDiff} disabled={searchLoading} onChange={(e) => handleInputChange("sqftDiff", e.target.value === "" ? "" : Number(e.target.value))}>
                  {sqftDiffOptions.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 md:col-span-3 lg:col-span-4 flex justify-center gap-2">
                <button className="flex items-center justify-center gap-1 px-4 py-1.5 rounded border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 ease-in-out duration-200 transition-all min-w-52 text-center disabled:cursor-not-allowed disabled:border-sky-300 disabled:bg-sky-300" type="submit" disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
                <button type="button" onClick={() => setFocusSubjectTrigger((t) => t + 1)} disabled={searchLoading} className="px-4 py-1.5 rounded border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 disabled:bg-emerald-300 disabled:border-emerald-300">
                  Focus Main Property
                </button>
              </div>
            </form>
          </div>

          {/* Main content: map + panel */}
          <div
            className={`
  grid grid-cols-1 md:grid-cols-12
  ${panelMode === "both" ? "gap-4" : "gap-0"}
  overflow-hidden flex-1 min-h-0 relative
`}
          >
            {/* Map */}
            <div
              className={`
  rounded-lg shadow overflow-hidden h-full relative
  ${showRightPanel ? "hidden md:block" : panelMode === "map" ? "col-span-12" : panelMode === "both" ? "col-span-12 md:col-span-7" : "hidden md:hidden"}
`}
            >
              <MapContainer center={mapCenter} zoom={subjectPosition ? 11 : 8} className="w-full h-full">
                <ResizeMap trigger={panelMode} />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />

                <FocusSubjectProperty position={subjectPosition} loading={loading} zoom={11} fallbackZoom={8} trigger={focusSubjectTrigger} />

                <OpenPopupOnFocus focusedPropertyId={focusedPropertyId} flyTrigger={flyTrigger} markerRefs={markerRefs} />

                {/* Fit bounds when owner selected and no focused address */}
                {selectedOwner && !focusedPropertyId && ownerPoints.length > 0 && <FitBounds points={ownerPoints} trigger={selectedOwner.key} zoom={14} />}

                {/* Star lines: from focused property to every other owner property */}
                {/* {selectedOwner &&
                  focusedPropertyId &&
                  ownerPoints.length > 1 &&
                  (() => {
                    // Find the focused address coordinates
                    const focusedAddr = selectedOwnerAddresses.find((a) => a.property_id === focusedPropertyId);
                    if (!focusedAddr) return null;
                    const focusPos = [focusedAddr.lat, focusedAddr.lng];
                    // For every other address, draw a line from focus to it
                    return selectedOwnerAddresses
                      .filter((a) => a.property_id !== focusedPropertyId) // exclude the focused one
                      .map((addr) => (
                        <Polyline
                          key={`star-${focusedPropertyId}-${addr.property_id}`}
                          positions={[focusPos, [addr.lat, addr.lng]]}
                          pathOptions={{
                            // color: "#16a34a", // green
                            color: "red",
                            weight: 1.5,
                            opacity: 0.8,
                            // dashArray: "6 6",
                            dashArray: null,
                          }}
                        />
                      ));
                  })()} */}

                {/* Subject property */}
                {subjectPosition && (
                  <>
                    <Circle center={subjectPosition} radius={searchRadius} pathOptions={{ color: "#dc2626", weight: 1.5, opacity: 0.6, fillColor: "#dc2626", fillOpacity: 0.08 }} />
                    <Marker position={subjectPosition} icon={subjectIcon}>
                      <Popup>
                        <div className="font-semibold">{headerLabel}</div>
                        <div className="text-xs text-gray-500">Subject property</div>
                      </Popup>
                    </Marker>
                  </>
                )}

                {/* Fly to a focused point */}
                {flyToPoint && <FlyToPoint point={flyToPoint} trigger={flyTrigger} zoom={18} />}

                {/* Connection lines: from the subject/main property out to every selected
                    owner's properties (or individually shift-clicked properties) */}
                {subjectPosition && connectedAddresses.length > 0 && connectedAddresses.map((a) => <Polyline key={`web-${a.property_id}`} positions={[subjectPosition, [a.lat, a.lng]]} pathOptions={{ color: "#dc2626", weight: 1.5, opacity: 0.8 }} />)}

                {/* All properties for the currently open scope(s) – shown as background layer so every
                    property at the county/zip/city level is visible, not just the selected owner's */}
                {allScopeAddresses.length > 0 && (
                  <MarkerClusterGroup chunkedLoading maxClusterRadius={50} spiderfyOnMaxZoom disableClusteringAtZoom={17} iconCreateFunction={createClusterIcon}>
                    {allScopeAddresses.map((addr, i) => {
                      const isFocused = focusedPropertyId === addr.property_id;
                      const isConnected = connectedAddresses.some((a) => a.property_id === addr.property_id);
                      const icon = isConnected ? connectedIcon : isFocused ? focusedIcon : iconFor(addr.groupKey);

                      return (
                        <Marker
                          key={`all-${addr.property_id ?? i}`}
                          ref={(el) => {
                            if (el && addr.property_id != null) markerRefs.current[addr.property_id] = el;
                          }}
                          position={[addr.lat, addr.lng]}
                          icon={icon}
                          eventHandlers={{
                            click: (e) => {
                              const mods = e.originalEvent;
                              if (mods && (mods.shiftKey || mods.metaKey || mods.ctrlKey)) {
                                toggleConnectAddress(addr);
                                return;
                              }
                              handleAddressClick(addr, addr.groupKey, addr.scopeKey, addr.rowIndex, addr.row);
                            },
                          }}
                        >
                          <Popup>
                            <div className="font-semibold">{addr.label || addr.address}</div>
                            <div className="text-xs text-gray-500">
                              {addr.city}, {addr.state} {addr.zip}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Found in {scopeLabels[addr.scopeKey]} {groupLabels[addr.groupKey]}
                            </div>
                            <div className={`text-xs mt-1 font-medium ${GROUP_COLORS[addr.groupKey].text}`}>{addr.row?.name}</div>
                            {/* <div className="text-[10px] text-gray-400 mt-1 italic">Shift-click to add to connection web</div> */}
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MarkerClusterGroup>
                )}

                {/* Owner addresses */}
                {selectedOwner && (
                  <MarkerClusterGroup ref={clusterGroupRef} chunkedLoading maxClusterRadius={50} spiderfyOnMaxZoom disableClusteringAtZoom={17} iconCreateFunction={createClusterIcon}>
                    {selectedOwnerAddresses.map((addr, i) => {
                      const isFocused = focusedPropertyId === addr.property_id;
                      const isConnected = connectedAddresses.some((a) => a.property_id === addr.property_id);
                      const icon = isConnected ? connectedIcon : isFocused ? focusedIcon : iconFor(selectedOwner.groupKey);

                      return (
                        <Marker
                          key={addr.property_id ?? i}
                          ref={(el) => {
                            if (el && addr.property_id != null) markerRefs.current[addr.property_id] = el;
                          }}
                          position={[addr.lat, addr.lng]}
                          icon={icon}
                          eventHandlers={{
                            click: (e) => {
                              const mods = e.originalEvent;
                              if (mods && (mods.shiftKey || mods.metaKey || mods.ctrlKey)) {
                                toggleConnectAddress({ ...addr, row: selectedOwner.row });
                                return;
                              }
                              handleMarkerClick(addr);
                              if (markerRefs.current[addr.property_id]) {
                                markerRefs.current[addr.property_id].openPopup();
                              }
                            },
                          }}
                        >
                          <Popup>
                            <div className="font-semibold">{addr.label || addr.address}</div>
                            <div className="text-xs text-gray-500">
                              {addr.city}, {addr.state} {addr.zip}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Found in {scopeLabels[selectedOwner.scopeKey]} {groupLabels[selectedOwner.groupKey]}
                            </div>
                            <div className={`text-xs mt-1 font-medium ${GROUP_COLORS[selectedOwner.groupKey].text}`}>{selectedOwner.row.name}</div>
                            {/* <div className="text-[10px] text-gray-400 mt-1 italic">Shift-click to add to connection web</div> */}
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MarkerClusterGroup>
                )}

                {subjectPosition && (
                  <button type="button" onClick={() => setFocusSubjectTrigger((t) => t + 1)} className="absolute bottom-6 right-4 z-[1000] bg-white rounded-full shadow-lg p-2 hover:bg-gray-100 transition-colors border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" disabled={loading || !subjectPosition} aria-label="Focus main property" title="Focus main property">
                    <Locate size={16} className="text-gray-700" />
                  </button>
                )}
              </MapContainer>

              {/* Connection web control */}
              {connectedAddresses.length > 0 && (
                <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 flex items-center gap-3 text-sm">
                  <span className="font-medium text-gray-700">
                    {connectedAddresses.length} propert{connectedAddresses.length === 1 ? "y" : "ies"} selected
                  </span>
                  <button type="button" onClick={clearConnectedAddresses} className="text-orange-600 hover:text-orange-700 font-semibold">
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* Right panel – conditionally show profile or list */}
            <div
              className={`
  bg-white rounded-lg shadow overflow-hidden h-full flex flex-col min-h-0
  ${showRightPanel ? "col-span-12 md:col-span-5" : panelMode === "data" ? "col-span-12" : panelMode === "both" ? "hidden md:flex md:col-span-5" : "hidden"}
`}
            >
              {selectedOwner ? (
                <OwnerProfilePanel selectedOwner={selectedOwner} onBack={() => setSelectedOwner(null)} onAddressClick={handleOwnerProfileAddressClick} focusedPropertyId={focusedPropertyId} connectedAddresses={connectedAddresses} onToggleConnectOwner={(row) => toggleConnectOwner(row, selectedOwner.key, selectedOwner.groupKey, selectedOwner.scopeKey, selectedOwner.rowIndex)} onToggleConnectProperty={(addr, row) => toggleConnectProperty(addr, row, selectedOwner.key, selectedOwner.groupKey, selectedOwner.scopeKey, selectedOwner.rowIndex)} />
              ) : (
                <>
                  <div className="flex flex-col h-full min-h-0">
                    <div className="flex items-baseline justify-between mb-3.5 sticky top-0 bg-white p-4 rounded-lg z-[1] border-b border-gray-100">
                      <h2 className="text-lg font-extrabold text-[#0B2436] m-0">{loading ? `Searching near ${headerLabel}...` : `Owners near ${headerLabel}`}</h2>
                      {connectedAddresses.length > 0 && (
                        <button type="button" onClick={downloadSelectedCSV} disabled={exporting} className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs border border-[#008080] bg-[#008080] text-white hover:bg-[#005252] hover:border-[#005252] disabled:bg-sky-200 disabled:text-black disabled:cursor-wait ease-in-out duration-200 transition-all text-nowrap">
                          <Download size={14} />
                          <span>{exporting ? "Exporting..." : `Export Selected (${connectedAddresses.length})`}</span>
                        </button>
                      )}
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto min-h-0">
                      {loading ? (
                        <>
                          <div className="h-10 bg-gray-100 rounded-lg mb-3 animate-pulse" />
                          <PropertyCardSkeleton />
                          <div className="h-10 bg-gray-100 rounded-lg my-3 animate-pulse" />
                          <PropertyCardSkeleton />
                        </>
                      ) : error ? (
                        <div className="border border-red-300 bg-red-50 text-red-700 px-4 py-3 rounded text-left text-sm">{error}</div>
                      ) : data ? (
                        <>
                          <SelectedOwnersSummary connectedAddresses={connectedAddresses} onClearOwner={clearOwnerConnections} onRemoveProperty={removeConnectedProperty} onClearAll={clearConnectedAddresses} onAddressClick={handleAddressClick} focusedPropertyId={focusedPropertyId} />
                          <OwnerGroup title="Flippers" groupKey="flippers" groupData={data.flippers} colors={GROUP_COLORS.flippers} isOpen={!!openGroups.flippers} onToggleGroup={() => handleToggleGroup("flippers")} openScopes={openScopes} onToggleScope={handleToggleScope} selectedKey={selectedOwner?.key} onSelectOwner={handleSelectOwner} loading={loading} expandedOwners={expandedOwners} onToggleExpandOwner={handleToggleExpandOwner} focusedPropertyId={focusedPropertyId} onAddressClick={handleAddressClick} connectedAddresses={connectedAddresses} onToggleConnectOwner={toggleConnectOwner} onToggleConnectProperty={toggleConnectProperty} />
                          <OwnerGroup title="Landlords" groupKey="landlords" groupData={data.landlords} colors={GROUP_COLORS.landlords} isOpen={!!openGroups.landlords} onToggleGroup={() => handleToggleGroup("landlords")} openScopes={openScopes} onToggleScope={handleToggleScope} selectedKey={selectedOwner?.key} onSelectOwner={handleSelectOwner} loading={loading} expandedOwners={expandedOwners} onToggleExpandOwner={handleToggleExpandOwner} focusedPropertyId={focusedPropertyId} onAddressClick={handleAddressClick} connectedAddresses={connectedAddresses} onToggleConnectOwner={toggleConnectOwner} onToggleConnectProperty={toggleConnectProperty} />
                        </>
                      ) : (
                        <div className="text-sm text-gray-500 text-center py-8">No data yet.</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {!showRightPanel && (
              <div
                className={`hidden md:flex absolute top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100 ${panelMode === "data" ? "left-0 translate-x-0" : panelMode === "map" ? "left-[100%] -translate-x-full" : "left-[58.3333%] -translate-x-1/2"} z-[9999]`}
                //   style={{ left: panelMode === "both" ? "58.3333%" : "50%" }}
              >
                <div className="flex overflow-hidden rounded-full border border-gray-300 bg-white shadow-lg">
                  <button onClick={() => handlePanelMode("data")} className={`p-2 transition ${panelMode === "data" ? "bg-sky-500 text-white" : "hover:bg-gray-100"}`} title="Expand Owner">
                    <ChevronLeft size={18} />
                  </button>

                  <div className="w-px bg-gray-200" />

                  <button onClick={() => handlePanelMode("map")} className={`p-2 transition ${panelMode === "map" ? "bg-sky-500 text-white" : "hover:bg-gray-100"}`} title="Expand Map">
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile toggle button */}
          <button onClick={() => setShowRightPanel((prev) => !prev)} className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 shadow-lg active:scale-95 z-[9999] flex items-center justify-center gap-1 px-4 py-1.5 rounded border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 ease-in-out duration-200 transition-all text-sm min-w-52">
            {showRightPanel ? "Show Map" : "Show Owners"}
          </button>
        </div>
      </div>
    </>
  );
}
